/**
 * WhatsAppNotificationToggle Component
 *
 * A compact toggle switch for enabling/disabling WhatsApp notifications
 * on a per-orchestration basis. Designed for use in workflow settings panels.
 *
 * Related Tickets: AC-1051
 */

import React, { useState } from 'react';
import { MessageSquare, Loader } from 'lucide-react';
import apiService from '../../services/api';

interface WhatsAppNotificationToggleProps {
  orchestrationId: number;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

const WhatsAppNotificationToggle: React.FC<WhatsAppNotificationToggleProps> = ({
  orchestrationId,
  enabled,
  onToggle,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = async () => {
    const newValue = !enabled;
    try {
      setIsLoading(true);
      setError(null);
      await apiService.whatsAppToggleOrchestrationNotify(orchestrationId, newValue);
      onToggle(newValue);
    } catch (err: any) {
      console.error('[WhatsAppNotificationToggle] Toggle failed:', err);
      setError('Failed to update');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between py-2">
      <div className="flex items-center min-w-0 mr-3">
        <MessageSquare className="w-4 h-4 text-green-600 dark:text-green-500 mr-2 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            WhatsApp Notifications
          </p>
          {error && (
            <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
          )}
        </div>
      </div>
      <button
        role="switch"
        aria-checked={enabled}
        aria-label="WhatsApp Notifications"
        disabled={isLoading}
        onClick={handleToggle}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${enabled ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-600'}`}
      >
        {isLoading ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <Loader className="w-3 h-3 text-white animate-spin" />
          </span>
        ) : (
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        )}
      </button>
    </div>
  );
};

export default WhatsAppNotificationToggle;
